import { useState, useEffect } from "react";
import { Container, Nav, Navbar } from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import "../../App.css";

// Image imports
import TopLogoLight from "../../images/logo-light.png";

export default function TopMenu() {
  const [activeLink, setActiveLink] = useState("");
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // === Scroll-based active link logic ===
  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
    setActiveLink(id);
  };

  const handleScroll = () => {
    const sections = document.querySelectorAll("section[id]");
    let currentSection = "";
    sections.forEach((section) => {
      const sectionTop = section.offsetTop;
      const sectionHeight = section.clientHeight;
      if (window.scrollY >= sectionTop - sectionHeight / 3) {
        currentSection = section.getAttribute("id");
      }
    });
    setActiveLink(currentSection);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location]);

  // === Sticky Navbar effect ===
  const [stickyClass, setStickyClass] = useState("");
  useEffect(() => {
    const stickNavbar = () => {
      setStickyClass(window.scrollY > 0 ? "sticky-nav" : "");
    };
    window.addEventListener("scroll", stickNavbar);
    return () => window.removeEventListener("scroll", stickNavbar);
  }, []);

  // === Framer Motion animation variants ===
  const navVariants = {
    hidden: { y: -100, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100, damping: 20 },
    },
  };

  const navItemVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.5 },
    }),
  };

  const logoVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { type: "spring", stiffness: 200, damping: 20 },
    },
  };

  // === Navigation ===
  let navText = "Home";
  let homeHref = "/home";
  if (location.pathname === "/home2") {
    navText = "Home 2";
    homeHref = "/home2";
  } else if (location.pathname === "/home3") {
    navText = "Home 3";
    homeHref = "/home3";
  }

  const handleNavigation = (item) => {
    if (item.link) {
      navigate(item.link);
      window.scrollTo(0, 0);
    } else {
      scrollToSection(item.id);
    }
    setExpanded(false);
  };

  const handleHomeNavigation = () => {
    navigate(homeHref);
    window.scrollTo(0, 0);
    setExpanded(false);
  };

  const handleMobileNavigation = (item) => {
    if (item.id === "home") {
      handleHomeNavigation();
    } else {
      handleNavigation(item);
    }
  };

  // === Google Form Redirect ===
  const handleGoogleFormRedirect = () => {
    window.open(
      "https://docs.google.com/forms/u/1/d/e/1FAIpQLSd_7Mh2uufrf7Jx_mRo14WTi14Z-hW0j3Ay9tZx4Czrw5AfLA/viewform", // 👈 Replace with your actual Google Form link
      "_blank"
    );
  };

  return (
    <>
      <motion.div
        className="top-custom-menu"
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={navVariants}
      >
        <Navbar
          collapseOnSelect
          expand="lg"
          className={`${stickyClass}`}
          expanded={expanded}
          onToggle={(expanded) => setExpanded(expanded)}
        >
          <Container>
            {/* Logo */}
            <motion.div variants={logoVariants} initial="hidden" animate="visible">
              <Link to={homeHref} className="topbar-logo">
                <img src={TopLogoLight} alt="tivikbuilders-logo" height={60} width={90} />
              </Link>
            </motion.div>

            <Navbar.Toggle aria-controls="responsive-navbar-nav" className="ms-3" />
            <Navbar.Collapse id="responsive-navbar-nav">
              <Nav className="mx-lg-auto main-navbar">
                <Nav className="navbar-nav">
                  {[
                    { text: navText, link: homeHref, id: "home" },
                    { text: "Property", id: "property" },
                    { text: "About", id: "about" },
                    { text: "Gallery", id: "gallery" },
                    { text: "Promotors", id: "Promotors" },
                  ].map((item, i) => (
                    <motion.div
                      key={item.id}
                      custom={i}
                      variants={navItemVariants}
                      initial="hidden"
                      animate="visible"
                    >
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Nav.Link
                          onClick={() => handleMobileNavigation(item)}
                          className={activeLink.includes(item.id) ? "active" : ""}
                          data-text={item.text}
                        >
                          <span>{item.text}</span>
                        </Nav.Link>
                      </motion.div>
                    </motion.div>
                  ))}

                  {/* Google Form Redirect Button */}
                  <motion.div
                    custom={5}
                    variants={navItemVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Nav.Link
                      onClick={handleGoogleFormRedirect}
                      data-text="YOUR NEW HOME AWAITS — BOOK TODAY"
                    >
                      <span>YOUR NEW HOME AWAITS — BOOK TODAY</span>
                    </Nav.Link>
                  </motion.div>

                  {/* Phone */}
                  <motion.div
                    custom={6}
                    variants={navItemVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                  <Nav.Link
                     href="tel:+18606820175"
                  >
                   📞 Call: +1 (860) 682-0175
                 </Nav.Link>
                 </motion.div>
                </Nav>
              </Nav>
            </Navbar.Collapse>
          </Container>
        </Navbar>
      </motion.div>
    </>
  );
}
